import React from 'react';
import { CalendarContainer as ReactDatePickerCalendarContainer } from 'react-datepicker';

import { css } from '@emotion/react';
import { styled } from '@mui/material';

import TimePicker from '../../TimePicker';
import { datePickerClasses } from '../theme';
import { usePickerConfig } from './PickerConfigContext';

import type { DatePickerContainerProps } from '../types';

export default function DatePickerContainer({
  children,
  showTime,
  showSeconds = true,
  showTimezone = false,
  timeValue,
  onTimeChange,
  monthsShown = 1,
  name,
  inline = false,
  minuteInterval,
  hourInterval,
  secondInterval,
  hideRangeHighlight = false,
  ...props
}: DatePickerContainerProps) {
  const pickerConfig = usePickerConfig();
  const calendarView = pickerConfig?.calendarView;
  const periodPickerDisabled = pickerConfig?.disabled ?? false;

  return (
    <StyledDatePickerContainer
      className={'kc-datepicker-container'}
      monthsShown={monthsShown}
      inline={inline}
      hideRangeHighlight={hideRangeHighlight}
      calendarView={calendarView}
      periodPickerDisabled={periodPickerDisabled}
    >
      <ReactDatePickerCalendarContainer {...props}>{children}</ReactDatePickerCalendarContainer>
      {showTime && (
        <StyledTimePickerContainer>
          <TimePicker
            name={name}
            value={timeValue || null}
            onChange={onTimeChange}
            showSeconds={showSeconds}
            showTimezone={showTimezone}
            fullWidth
            size={'lg'}
            minuteInterval={minuteInterval}
            hourInterval={hourInterval}
            secondInterval={secondInterval}
          />
        </StyledTimePickerContainer>
      )}
    </StyledDatePickerContainer>
  );
}

const StyledTimePickerContainer = styled('div', {
  target: datePickerClasses.timePickerContainer,
})`
  position: relative;
  padding: 8px;
  overflow: visible;
`;

const StyledDatePickerContainer = styled('div', {
  target: datePickerClasses.container,
  shouldForwardProp: prop =>
    prop !== 'inline' &&
    prop !== 'monthsShown' &&
    prop !== 'hideRangeHighlight' &&
    prop !== 'calendarView' &&
    prop !== 'periodPickerDisabled',
})<{
  hideRangeHighlight: boolean;
  inline: boolean;
  monthsShown: number;
  periodPickerDisabled: boolean;
  calendarView?: 'year' | 'year-month' | 'quarter';
}>`
  ${({ theme, monthsShown, inline, hideRangeHighlight, calendarView, periodPickerDisabled }) => {
    const singleMonthWidth = 336;
    const paddingY = 8 * 2;
    const gap = 12;
    const gapTotal = monthsShown > 1 ? gap * (monthsShown - 1) : 0;
    const containerWidth = calendarView
      ? singleMonthWidth + paddingY
      : singleMonthWidth * monthsShown + gapTotal + paddingY;

    return css`
      width: ${containerWidth}px;
      background-color: ${theme.color['surface/default']};
      overflow: visible;
      ${!inline && `box-shadow: ${theme.elevation['level_2']};`}

      .react-datepicker {
        padding: 8px;
        border: none;
        border-radius: 0;
        font-family: inherit;
        display: flex;

        .react-datepicker__month-container {
          float: none;
          margin-right: ${monthsShown > 1 ? `${gap}px` : '0'};

          &:last-of-type {
            margin-right: 0;
          }

          ${!hideRangeHighlight &&
          css`
            &:not(:has(.react-datepicker__day:hover)) {
              .react-datepicker__day--in-selecting-range:not(
                  .react-datepicker__day--selecting-range-start
                ):not(.react-datepicker__day--outside-month),
              .react-datepicker__day--selecting-range-end:not(
                  .react-datepicker__day--selecting-range-start
                ):not(.react-datepicker__day--outside-month) {
                background-color: ${theme.color['surface/default']} !important;
                color: ${theme.color['text/default']} !important;
              }

              .react-datepicker__day--today.react-datepicker__day--in-selecting-range:not(
                  .react-datepicker__day--selecting-range-start
                ):not(.react-datepicker__day--outside-month),
              .react-datepicker__day--today.react-datepicker__day--selecting-range-end:not(
                  .react-datepicker__day--selecting-range-start
                ):not(.react-datepicker__day--outside-month) {
                border: 1px solid ${theme.color['border/strongest/default']} !important;
              }
            }
          `}
        }

        .react-datepicker__header {
          padding: 0;
          border: none;
          border-radius: 0;
          background-color: transparent;

          .react-datepicker__current-month {
            display: none;
          }
        }

        .react-datepicker__day-names {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0;
          margin: 0;
          border: none;

          .react-datepicker__day-name {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 48px;
            height: 48px;
            margin: 0;

            > span {
              ${theme.typography['label/regular/md']};
            }
          }
        }

        .react-datepicker__month {
          margin: 0;
          padding: 0;
          ${inline &&
          `
            /* inline 모드에서 4주 기준 최소 높이 설정 (48px * 4 = 192px) */
            min-height: 192px;
          `}

          .react-datepicker__week {
            display: flex;
            align-items: center;
            justify-content: space-between;
            margin: 0;

            .react-datepicker__day {
              display: flex;
              align-items: center;
              justify-content: center;
              width: 48px;
              height: 48px;
              margin: 0;
              padding: 0;
              position: relative;
              border-radius: 0;
              background-color: ${theme.color['surface/default']};

              ${theme.typography['label/regular/md']};

              outline: none;

              cursor: pointer;
              transition: all 0.2s ease-in-out;

              &:hover {
                background-color: ${theme.color['surface/neutral/weak/default']};
                color: ${theme.color['text/default']};
              }

              &.react-datepicker__day--keyboard-selected {
                border: none;
                background-color: transparent;
                color: ${theme.color['text/default']};

                ${theme.typography['label/regular/md']};
                ${theme.mixins.focusRing('inside', {
                  borderRadius: 0,
                })};

                &:hover {
                  background-color: ${theme.color['surface/neutral/weak/default']};
                  color: ${theme.color['text/default']};
                }
              }

              &.kc-datepicker__day--range-anchor {
                border: none;
                background-color: ${theme.color['surface/neutral/stronger/default']};
                color: ${theme.color['text/inverse/default']};

                ${theme.typography['label/regular/md']};

                &:hover {
                  background-color: ${theme.color['surface/neutral/stronger/default']};
                  color: ${theme.color['text/inverse/default']};
                }
              }

              ${hideRangeHighlight &&
              css`
                &.react-datepicker__day--in-selecting-range,
                &.react-datepicker__day--selecting-range-start,
                &.react-datepicker__day--selecting-range-end {
                  border: none;
                  background-color: transparent;
                  color: ${theme.color['text/default']};

                  ${theme.typography['label/regular/md']};

                  &:hover {
                    background-color: ${theme.color['surface/neutral/weak/default']};
                    color: ${theme.color['text/default']};
                  }
                }
              `}

              &.react-datepicker__day--today {
                border: 1px solid ${theme.color['border/strongest/default']};
                background-color: transparent;
                color: ${theme.color['text/default']};

                ${theme.typography['label/regular/md']};

                &:hover {
                  background-color: transparent;
                  color: ${theme.color['text/default']};
                }
              }

              &.react-datepicker__day--in-range {
                border: none;
                background-color: ${hideRangeHighlight
                  ? theme.color['surface/default']
                  : theme.color['surface/neutral/weak/default']};
                color: ${theme.color['text/default']};

                ${theme.typography['label/regular/md']};

                &:hover {
                  background-color: ${theme.color['surface/neutral/weak/default']};
                  color: ${theme.color['text/default']};
                }
              }

              &.react-datepicker__day--selected,
              &.react-datepicker__day--range-start,
              &.react-datepicker__day--range-end {
                border: none;
                background-color: ${theme.color['surface/neutral/stronger/default']};
                color: ${theme.color['text/inverse/default']};

                ${theme.typography['label/regular/md']};

                &:hover {
                  background-color: ${theme.color['surface/neutral/stronger/default']};
                  color: ${theme.color['text/inverse/default']};
                }
              }

              ${!hideRangeHighlight &&
              css`
                &.react-datepicker__day--in-selecting-range {
                  border: none;
                  background-color: ${theme.color['surface/neutral/weak/default']};
                  color: ${theme.color['text/default']};

                  ${theme.typography['label/regular/md']};

                  &:hover {
                    background-color: ${theme.color['surface/neutral/weak/default']};
                    color: ${theme.color['text/default']};
                  }
                }

                &.react-datepicker__day--selecting-range-start,
                &.react-datepicker__day--selecting-range-end {
                  border: none;
                  background-color: ${theme.color['surface/neutral/stronger/default']};
                  color: ${theme.color['text/inverse/default']};

                  ${theme.typography['label/regular/md']};

                  &:hover {
                    background-color: ${theme.color['surface/neutral/stronger/default']};
                    color: ${theme.color['text/inverse/default']};
                  }
                }
              `}

              &.react-datepicker__day--disabled {
                border: none;
                background-color: transparent;
                color: ${theme.color['text/weakest/default']};
                cursor: not-allowed;

                &::before {
                  content: none;
                }

                &:hover {
                  background-color: transparent;
                  color: ${theme.color['text/weakest/default']};
                }
              }

              /* outside-month 상태일 때는 선택/범위 스타일 무시 */

              &.react-datepicker__day--outside-month {
                border: none;
                background-color: transparent !important;
                color: ${theme.color['text/weakest/default']} !important;

                &::before {
                  content: none;
                }

                &:hover {
                  background-color: transparent !important;
                  color: ${theme.color['text/weakest/default']} !important;
                }
              }
            }
          }
        }

        ${calendarView &&
        css`
          .react-datepicker__year,
          .react-datepicker__month {
            width: 336px;
            margin: 0;
            padding: 0;
          }

          .react-datepicker__year-wrapper {
            display: grid;
            grid-template-columns: repeat(4, minmax(0, 1fr));
            width: 336px;
            max-width: none;
          }

          .react-datepicker__month-text,
          .react-datepicker__quarter-text,
          .react-datepicker__year-text {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            box-sizing: border-box;
            width: 84px;
            height: 48px;
            margin: 0;
            padding: 0;
            border: none;
            border-radius: 0;
            background-color: ${theme.color['surface/default']};

            ${theme.typography['label/regular/md']};

            color: ${theme.color['text/default']};
            cursor: pointer;
            transition: all 0.2s ease-in-out;

            &:not([aria-disabled='true']):hover {
              border-radius: 0;
              background-color: ${theme.color['surface/neutral/weak/default']};
              color: ${theme.color['text/default']};
            }

            &.react-datepicker__month-text--keyboard-selected,
            &.react-datepicker__quarter-text--keyboard-selected,
            &.react-datepicker__year-text--keyboard-selected {
              background-color: transparent;
              color: ${theme.color['text/default']};

              ${theme.mixins.focusRing('inside', { borderRadius: 0 })};
            }

            &.react-datepicker__month-text--in-range,
            &.react-datepicker__month-text--in-selecting-range,
            &.react-datepicker__quarter-text--in-range,
            &.react-datepicker__quarter-text--in-selecting-range,
            &.react-datepicker__year-text--in-range,
            &.react-datepicker__year-text--in-selecting-range {
              background-color: ${theme.color['surface/neutral/weak/default']};
              color: ${theme.color['text/default']};
            }

            &.react-datepicker__month-text--selected,
            &.react-datepicker__month-text--range-start,
            &.react-datepicker__month-text--range-end,
            &.react-datepicker__quarter-text--selected,
            &.react-datepicker__quarter-text--range-start,
            &.react-datepicker__quarter-text--range-end,
            &.react-datepicker__year-text--selected,
            &.react-datepicker__year-text--range-start,
            &.react-datepicker__year-text--range-end {
              background-color: ${theme.color['surface/neutral/stronger/default']};
              color: ${theme.color['text/inverse/default']};

              &:hover {
                background-color: ${theme.color['surface/neutral/stronger/default']};
                color: ${theme.color['text/inverse/default']};
              }
            }

            &.react-datepicker__month-text--selecting-range-start,
            &.react-datepicker__month-text--selecting-range-end,
            &.react-datepicker__quarter-text--selecting-range-start,
            &.react-datepicker__quarter-text--selecting-range-end,
            &.react-datepicker__year-text--selecting-range-start,
            &.react-datepicker__year-text--selecting-range-end {
              background-color: ${theme.color['surface/neutral/stronger/default']};
              color: ${theme.color['text/inverse/default']};
            }

            &.react-datepicker__month-text--disabled,
            &.react-datepicker__quarter-text--disabled,
            &.react-datepicker__year-text--disabled {
              background-color: transparent;
              color: ${theme.color['text/weakest/default']};
              cursor: not-allowed;

              &:hover {
                background-color: transparent;
                color: ${theme.color['text/weakest/default']};
              }
            }
          }

          ${periodPickerDisabled &&
          css`
            .react-datepicker__month-text,
            .react-datepicker__quarter-text,
            .react-datepicker__year-text {
              background-color: transparent;
              color: ${theme.color['text/weakest/default']};
              cursor: not-allowed;
              pointer-events: none;
            }
          `}
        `}
      }
    `;
  }}
`;
